import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";
import {
  Table,
  Button,
  Form,
  Input,
  message,
  Empty,
  Drawer,
  Spin,
  Dropdown,
  Modal,
  InputNumber,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  MoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useCustomerService, useSectionAccess } from "../../Services";
import { Customer, CustomerAction } from "../../Types";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import {
  PageHeader,
  tablePagination,
  DRAWER_FORM_WIDTH,
  drawerFooter,
  drawerTitle,
} from "../../components";

export const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [form] = Form.useForm();

  // Retrieve customer queries and mutations from existing service hook
  const {
    customersQuery,
    createCustomerMutation,
    updateCustomerMutation,
    deleteCustomerMutation,
  } = useCustomerService();

  const { data: customerResponse, isLoading, refetch } = customersQuery;
  const { isAllowed } = useSectionAccess("customers");

  const rawCustomers: Customer[] = customerResponse?.data || [];

  // Filter customers on client-side
  const filteredCustomers = rawCustomers.filter((c: Customer) => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      c.name?.toLowerCase().includes(query) ||
      c.phone?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.address?.toLowerCase().includes(query)
    );
  });

  const total = filteredCustomers.length;

  // Paginate on client-side since API returns all customers at once
  const paginatedCustomers = filteredCustomers.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const openDrawer = (customer?: Customer) => {
    if (customer) {
      setEditingCustomer(customer);
      form.setFieldsValue({
        name: customer.name,
        phone: customer.phone,
        email: customer.email || "",
        address: customer.address || "",
        credit_limit: customer.credit_limit || 0,
      });
    } else {
      setEditingCustomer(null);
      form.resetFields();
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingCustomer(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const mutation = editingCustomer
        ? updateCustomerMutation
        : createCustomerMutation;

      const payload = editingCustomer
        ? { id: editingCustomer.id, ...values }
        : values;

      const res = await mutation.mutateAsync(payload);

      if (res?.success) {
        message.success(
          editingCustomer
            ? "Customer updated successfully!"
            : "Customer created successfully!",
        );
        closeDrawer();
      } else {
        if (res?.errors) {
          Object.entries(res.errors).forEach(([field, msg]) => {
            form.setFields([{ name: field, errors: [msg as string] }]);
          });
        } else {
          message.error(res?.message || "Operation failed.");
        }
      }
    } catch (error) {
      message.error("Please fill all the required fields.");
    }
  };

  const handleDelete = async (customer: Customer) => {
    try {
      const res = await deleteCustomerMutation.mutateAsync(String(customer.id));
      if (res?.success) {
        message.success("Customer deleted successfully!");
        if (paginatedCustomers.length === 1 && page > 1) {
          setPage(page - 1);
        }
      } else {
        message.error(res?.message || "Failed to delete customer.");
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to delete customer.");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setPage(1);
  };

  const columns = [
    {
      title: "Customer",
      key: "name",
      render: (_: any, rec: Customer) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <span className="text-gray-600 font-semibold text-xs">
              {rec.name[0]?.toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium text-gray-800 text-sm m-0 leading-tight">
              {rec.name}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (v: string) =>
        v ? (
          <span className="text-sm text-gray-700">{v}</span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (v: string) =>
        v ? (
          <span className="text-sm text-gray-700">{v}</span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (v: string) =>
        v ? (
          <span className="text-sm text-gray-700">{v}</span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: "Credit Limit",
      dataIndex: "credit_limit",
      key: "credit_limit",
      render: (v: number) =>
        v !== undefined && v !== null ? (
          <span className="text-sm font-semibold text-gray-700">
            Rs. {Number(v).toFixed(2)}
          </span>
        ) : (
          <span className="text-gray-300">Rs. 0.00</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center" as const,
      render: (_: any, rec: Customer) => {
        const menuItems = [
          ...(isAllowed(CustomerAction.Edit)
            ? [
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: () => openDrawer(rec),
                },
              ]
            : []),
          {
            key: "ledger",
            label: "View Ledger",
            icon: <EyeOutlined />,
            onClick: () => navigate(`/system/customer_ledger?id=${rec.id}`),
          },
          ...(isAllowed(CustomerAction.Delete)
            ? [
                {
                  key: "delete",
                  label: "Delete",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: "Confirm Deletion",
                      content: "Are you sure you want to delete this customer?",
                      okText: "Yes",
                      okType: "danger",
                      cancelText: "No",
                      onOk: () => handleDelete(rec),
                    });
                  },
                },
              ]
            : []),
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              size="small"
              className="text-gray-400"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Customer Management"
        subText={`Total: ${total} customers`}
        handleButtons={[
          ...(isAllowed(CustomerAction.Create)
            ? [
                {
                  title: "Add Customer",
                  icon: PlusOutlined,
                  onClick: () => {
                    openDrawer();
                  },
                },
              ]
            : []),
          {
            title: "Refresh",
            icon: ReloadOutlined,
            onClick: () => refetch(),
          },
        ]}
        searchBar={
          <Input
            placeholder="Search by name, phone, email, address…"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={handleSearchChange}
            allowClear
            style={{ width: 300 }}
          />
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <Table
          dataSource={paginatedCustomers}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            itemName: "customers",
          })}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No customers found"
              />
            ),
          }}
        />
      </div>

      <Drawer
        title={drawerTitle(
          editingCustomer ? "Edit Customer" : "Add New Customer",
        )}
        open={drawerOpen}
        onClose={closeDrawer}
        width={DRAWER_FORM_WIDTH}
        footer={drawerFooter({
          actions: [
            { label: "Cancel", onClick: closeDrawer },
            {
              label: editingCustomer ? "Save Changes" : "Create Customer",
              type: "primary",
              loading:
                createCustomerMutation.isPending ||
                updateCustomerMutation.isPending,
              onClick: handleSubmit,
            },
          ],
        })}
      >
        <Spin
          spinning={
            createCustomerMutation.isPending || updateCustomerMutation.isPending
          }
        >
          <Form form={form} layout="vertical">
            <Form.Item
              label="Full Name"
              name="name"
              rules={[
                { required: true, message: "Please enter customer name." },
              ]}
            >
              <Input placeholder="Enter full name" />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="phone"
              rules={[
                { required: true, message: "Please enter phone number." },
                {
                  pattern: /^[0-9+-\s()]*$/,
                  message: "Please enter a valid phone number.",
                },
              ]}
            >
              <Input placeholder="Enter phone number" />
            </Form.Item>

            <Form.Item
              label="Email Address (Optional)"
              name="email"
              rules={[
                {
                  type: "email",
                  message: "Please enter a valid email address.",
                },
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Form.Item label="Physical Address (Optional)" name="address">
              <Input.TextArea
                placeholder="Enter address details"
                rows={3}
                style={{ resize: "none" }}
              />
            </Form.Item>

            <Form.Item
              label="Credit Limit"
              name="credit_limit"
              rules={[
                {
                  type: "number",
                  min: 0,
                  message: "Credit limit must be a positive number.",
                },
              ]}
            >
              <InputNumber
                placeholder="Enter credit limit"
                className="w-full"
                min={0}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Form>
        </Spin>
      </Drawer>
    </div>
  );
};
